import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowLeft, ArrowRight, Award, Target, Clock, Zap, BookOpen, Cpu } from 'react-feather';
import confetti from 'canvas-confetti';
import styles from '../styles/professionalQuiz.module.css';
import { recordQuizResult } from './ProgressDashboard';

/**
 * Enhanced Quiz Deck with modern UI and animations
 * Props: quiz object { title, questions:[{prompt,choices,answers:[idx]}] }
 */
const EnhancedQuizDeck = ({ quiz, onComplete, onExit }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({}); // Now single values, not arrays
  const [showFeedback, setShowFeedback] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [streak, setStreak] = useState(0);
  const [timePerQuestion, setTimePerQuestion] = useState({});
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [answersLocked, setAnswersLocked] = useState(false);
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [totalTime, setTotalTime] = useState(0);
  const [quizStartTime, setQuizStartTime] = useState(null);
  
  const progressBarRef = useRef(null);

  useEffect(() => {
    // Reset timer when question changes
    setQuestionStartTime(Date.now());
    setShowFeedback(false);
    setAnswersLocked(false);
  }, [currentQuestion]);

  useEffect(() => {
    // Update total time if timer is enabled
    if (timerEnabled && quizStartTime && !showResults && !showReview) {
      const interval = setInterval(() => {
        setTotalTime(Math.floor((Date.now() - quizStartTime) / 1000));
      }, 100);
      return () => clearInterval(interval);
    }
  }, [timerEnabled, showResults, showReview, quizStartTime]);

  const toggleTimer = () => {
    if (!timerEnabled) {
      // Start timer
      setQuizStartTime(Date.now());
      setTimerEnabled(true);
      setTotalTime(0);
    } else {
      // Stop timer
      setTimerEnabled(false);
    }
  };

  if (!quiz || !quiz.questions || quiz.questions.length === 0) {
    return (
      <div className={styles.errorWrapper}>
        <Cpu size={48} className={styles.errorIcon} />
        <h3>No Quiz Data Available</h3>
        <p>Unable to load quiz questions. Please try again.</p>
        <button onClick={onExit} className={styles.primaryBtn}>
          Go Back
        </button>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];
  const userAnswers = selectedAnswers[currentQuestion]; // Don't default to [] - 0 is a valid selection!

  // Normalize question structure - handle both original and Gemini formats
  const normalizedQuestion = question ? {
    prompt: question.question || question.prompt || `Question ${currentQuestion + 1}`,
    choices: question.answerOptions ? 
      question.answerOptions.map(opt => opt.text || opt) : 
      (question.choices || []).map(choice => typeof choice === 'string' ? choice : String(choice)),
    answers: question.answerOptions ? 
      question.answerOptions.map((opt, idx) => opt.isCorrect ? idx : null).filter(idx => idx !== null) :
      (question.answers || question.correctAnswerIds || []).filter(ans => typeof ans === 'number' && ans >= 0),
    explanation: question.rationale || question.explanation || ''
  } : null;

  // Debug logging for question structure
  console.log('EnhancedQuizDeck Debug:', {
    currentQuestion,
    originalQuestion: question,
    normalizedQuestion,
    quizTitle: quiz?.title,
    hasExplanation: !!normalizedQuestion?.explanation,
    explanationContent: normalizedQuestion?.explanation
  });

  // Safety check for question structure
  if (!normalizedQuestion || !normalizedQuestion.prompt) {
    return (
      <div className={styles.errorWrapper}>
        <Cpu size={48} className={styles.errorIcon} />
        <h3>Invalid Question Data</h3>
        <p>This question appears to be missing required data. Please try again.</p>
        <button onClick={onExit} className={styles.primaryBtn}>
          Go Back
        </button>
      </div>
    );
  }

  // Skip questions without choices (like fill-in-the-blank) and move to next question
  if (!normalizedQuestion.choices || !Array.isArray(normalizedQuestion.choices) || normalizedQuestion.choices.length === 0) {
    console.log('Skipping question without choices:', normalizedQuestion.prompt);
    // Auto-advance to next question
    if (currentQuestion < (quiz.questions || []).length - 1) {
      setTimeout(() => setCurrentQuestion(currentQuestion + 1), 100);
    } else {
      // No more questions, show results
      setTimeout(() => calculateResults(), 100);
    }
    
    return (
      <div className={styles.questionCard}>
        <div className={styles.questionPrompt}>
          <BookOpen size={20} className={styles.questionIcon} />
          <p>{normalizedQuestion.prompt}</p>
        </div>
        <div className={styles.skipMessage}>
          <p>This question type is not supported in the quiz interface. Skipping to next question...</p>
        </div>
      </div>
    );
  }

  // Normalize correct answers
  const normalizeCorrectIndices = (q) => {
    const ans = q?.answers ?? q?.correctAnswerIds ?? [];
    const choices = q?.choices ?? [];
    if (!Array.isArray(ans)) return [];

    if (ans.every(a => typeof a === 'number' && Number.isFinite(a))) {
      const max = Math.max(...ans);
      const min = Math.min(...ans);
      const isOneBased = min >= 1 && max === choices.length && !ans.includes(0);
      return ans.map(idx => {
        const normalized = isOneBased ? idx - 1 : idx;
        return Math.max(0, Math.min(choices.length - 1, normalized));
      });
    }
    return [];
  };

  const normalizedCorrect = normalizeCorrectIndices(normalizedQuestion);

  const handleAnswerChange = (choiceIndex) => {
    // Prevent changes after feedback is shown
    if (answersLocked) return;
    
    // Single selection only
    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestion]: choiceIndex
    }));
  };

  const handleSubmit = () => {
    if (userAnswers === undefined || answersLocked) return;
    
    const isCorrect = checkAnswer(userAnswers);
    setShowFeedback(true);
    setAnswersLocked(true);
    
    // Record time spent on this question
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setTimePerQuestion(prev => ({
      ...prev,
      [currentQuestion]: timeSpent
    }));
    
    if (isCorrect) {
      setStreak(prev => prev + 1);
    } else {
      setStreak(0);
    }
  };

  const checkAnswer = (answer = userAnswers) => {
    // Single selection check
    if (answer === undefined) return false;
    return normalizedCorrect.includes(answer);
  };

  const triggerCelebration = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);

      const particleCount = 50 * (timeLeft / duration);
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const nextQuestion = () => {
    // Save time spent on this question
    const timeSpent = Math.round((Date.now() - questionStartTime) / 1000);
    setTimePerQuestion(prev => ({
      ...prev,
      [currentQuestion]: timeSpent
    }));

    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      calculateResults();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateResults = () => {
    const results = [];
    
    (quiz.questions || []).forEach((q, idx) => {
      // Normalize each question for consistent processing
      const normalizedQ = {
        prompt: q.question || q.prompt || `Question ${idx + 1}`,
        choices: q.answerOptions ? 
          q.answerOptions.map(opt => opt.text || opt) : 
          (q.choices || []).map(choice => typeof choice === 'string' ? choice : String(choice)),
        answers: q.answerOptions ? 
          q.answerOptions.map((opt, i) => opt.isCorrect ? i : null).filter(i => i !== null) :
          (q.answers || q.correctAnswerIds || []).filter(ans => typeof ans === 'number' && ans >= 0),
      };
      
      // Skip questions without choices
      if (!normalizedQ.choices || normalizedQ.choices.length === 0) return;
      
      const correctAnswers = normalizeCorrectIndices(normalizedQ);
      const userAns = selectedAnswers[idx];
      const isCorrect = userAns !== undefined && correctAnswers.includes(userAns);
      
      results.push({
        question: normalizedQ.prompt,
        userAnswer: userAns !== undefined ? (normalizedQ.choices[userAns] || 'Unknown') : 'Skipped',
        correctAnswer: correctAnswers.map(i => normalizedQ.choices[i] || 'Unknown').join(', '),
        isCorrect,
        timeSpent: timePerQuestion[idx] || 0
      });
    });

    // Trigger celebration at the end for any completion
    triggerCelebration();

    setShowResults(true);

    // Record result in progress dashboard
    const score = results.filter(r => r.isCorrect).length;
    recordQuizResult(score, results.length, quiz.title || '');

    if (onComplete) {
      onComplete(results);
    }
  };

  const resetQuiz = () => {
    setShowResults(false);
    setShowReview(false);
    setCurrentQuestion(0);
    setSelectedAnswers({});
    setTimePerQuestion({});
    setStreak(0);
    setShowFeedback(false);
    setAnswersLocked(false);
  };

  // Review Screen
  if (showReview) {
    return (
      <div className={styles.quizWrapper}>
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <div className={styles.titleSection}>
              <h1 className={styles.quizTitle}>Review Your Answers</h1>
              <p className={styles.quizSubtitle}>
                See how you did on each question
              </p>
            </div>
            <button className={styles.secondaryBtn} onClick={() => setShowReview(false)}>
              Back to Results
            </button>
          </div>
        </div>
        
        <div className={styles.reviewList}>
          {(quiz.questions || []).map((q, idx) => {
            const normalizedQ = {
              prompt: q.question || q.prompt || `Question ${idx + 1}`,
              choices: q.answerOptions ? 
                q.answerOptions.map(opt => opt.text || opt) : 
                (q.choices || []).map(choice => typeof choice === 'string' ? choice : String(choice)),
              answers: q.answerOptions ? 
                q.answerOptions.map((opt, i) => opt.isCorrect ? i : null).filter(i => i !== null) :
                (q.answers || q.correctAnswerIds || []).filter(ans => typeof ans === 'number' && ans >= 0),
              explanation: q.rationale || q.explanation || ''
            };
            
            if (!normalizedQ.choices || normalizedQ.choices.length === 0) return null;
            
            const correctAnswers = normalizeCorrectIndices(normalizedQ);
            const userAns = selectedAnswers[idx];
            const isCorrect = userAns !== undefined && correctAnswers.includes(userAns);
            
            return (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewHeader}>
                  <span className={styles.questionNumber}>Question {idx + 1}</span>
                  <span className={`${styles.reviewBadge} ${isCorrect ? styles.correct : styles.incorrect}`}>
                    {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                </div>
                
                <h3 className={styles.reviewQuestion}>{normalizedQ.prompt}</h3>
                
                <div className={styles.reviewChoices}>
                  {normalizedQ.choices.map((choice, choiceIdx) => {
                    const isUserAnswer = userAns === choiceIdx;
                    const isCorrectAnswer = correctAnswers.includes(choiceIdx);
                    
                    return (
                      <div 
                        key={choiceIdx}
                        className={`${styles.reviewChoice} ${
                          isCorrectAnswer ? styles.correct : ''
                        } ${isUserAnswer && !isCorrectAnswer ? styles.incorrect : ''}`}
                      >
                        <span className={styles.reviewChoiceIcon}>
                          {isCorrectAnswer ? '✓' : isUserAnswer ? '✗' : ''}
                        </span>
                        <span>{choice}</span>
                        {isUserAnswer && <span className={styles.yourAnswer}>(Your answer)</span>}
                      </div>
                    );
                  })}
                </div>
                
                {normalizedQ.explanation && (
                  <div className={styles.reviewExplanation}>
                    <strong>Explanation:</strong> {normalizedQ.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        <div className={styles.reviewFooter}>
          <button className={styles.primaryBtn} onClick={() => setShowReview(false)}>
            Back to Results
          </button>
          <button className={styles.secondaryBtn} onClick={resetQuiz}>
            Retake Quiz
          </button>
        </div>
      </div>
    );
  }

  // Results Screen
  if (showResults) {
    // Calculate score using the same logic as calculateResults
    let score = 0;
    let totalProcessed = 0;
    
    (quiz.questions || []).forEach((q, idx) => {
      // Normalize each question for consistent processing
      const normalizedQ = {
        choices: q.answerOptions ? 
          q.answerOptions.map(opt => opt.text || opt) : 
          (q.choices || []).map(choice => typeof choice === 'string' ? choice : String(choice)),
        answers: q.answerOptions ? 
          q.answerOptions.map((opt, i) => opt.isCorrect ? i : null).filter(i => i !== null) :
          (q.answers || q.correctAnswerIds || []).filter(ans => typeof ans === 'number' && ans >= 0),
      };
      
      // Skip questions without choices
      if (!normalizedQ.choices || normalizedQ.choices.length === 0) return;
      
      totalProcessed++;
      const correctAnswers = normalizeCorrectIndices(normalizedQ);
      const userAns = selectedAnswers[idx];
      const isCorrect = userAns !== undefined && correctAnswers.includes(userAns);
      
      if (isCorrect) score++;
    });
    
    const percentage = totalProcessed > 0 ? Math.round((score / totalProcessed) * 100) : 0;
    const totalTime = Object.values(timePerQuestion).reduce((a, b) => a + b, 0);
    
    return (
      <motion.div 
        className={styles.resultsWrapper}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          className={styles.resultsIcon}
          initial={{ rotate: -180, scale: 0 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ duration: 0.5, type: "spring" }}
        >
          <Award size={80} />
        </motion.div>
        <h1 className={styles.resultsTitle}>Quiz Complete!</h1>
        <p className={styles.resultsScore}>
          {score}/{totalProcessed} Correct • {percentage}%
        </p>

        <div className={styles.resultsStats}>
          <motion.div 
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Check className={styles.statIcon} size={24} />
            <div className={styles.statValue}>{percentage}%</div>
            <div className={styles.statLabel}>Score</div>
          </motion.div>
          
          <motion.div 
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Target className={styles.statIcon} size={24} />
            <div className={styles.statValue}>{score}/{totalProcessed}</div>
            <div className={styles.statLabel}>Correct</div>
          </motion.div>
          
          <motion.div 
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Clock className={styles.statIcon} size={24} />
            <div className={styles.statValue}>{Math.floor(totalTime / 60)}:{String(totalTime % 60).padStart(2, '0')}</div>
            <div className={styles.statLabel}>Time</div>
          </motion.div>
          
          <motion.div 
            className={styles.statCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Zap className={styles.statIcon} size={24} />
            <div className={styles.statValue}>{streak}</div>
            <div className={styles.statLabel}>Best Streak</div>
          </motion.div>
        </div>
        
        <div className={styles.resultsActions}>
          <motion.button 
            className={styles.primaryBtn} 
            onClick={resetQuiz}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, type: "spring", stiffness: 300 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Target size={20} />
            Retake Quiz
          </motion.button>
          
          <motion.button 
            className={styles.secondaryBtn} 
            onClick={() => setShowReview(true)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, type: "spring", stiffness: 300 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <BookOpen size={20} />
            Review Answers
          </motion.button>
          
          {onExit && (
            <motion.button 
              className={styles.ghostBtn} 
              onClick={onExit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, type: "spring", stiffness: 300 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Exit Quiz
            </motion.button>
          )}
        </div>
      </motion.div>
    );
  }

  // Quiz Question Screen
  return (
    <div className={styles.quizWrapper}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleSection}>
            <h1 className={styles.quizTitle}>{quiz.title || 'Practice Quiz'}</h1>
            <p className={styles.quizSubtitle}>
              Test your knowledge with {(quiz.questions || []).length} questions
            </p>
          </div>
          <div className={styles.headerActions}>
            {streak > 0 && (
              <motion.div 
                className={styles.streak}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring" }}
              >
                <Zap size={16} />
                <span>{streak} Streak!</span>
              </motion.div>
            )}
            
            <button 
              className={`${styles.timerToggle} ${timerEnabled ? styles.timerActive : ''}`}
              onClick={toggleTimer}
              title={timerEnabled ? "Stop Timer" : "Start Timer"}
            >
              <Clock size={16} />
              {timerEnabled && (
                <span>{Math.floor(totalTime / 60)}:{String(totalTime % 60).padStart(2, '0')}</span>
              )}
            </button>
          </div>
        </div>
        
        <div className={styles.progressSection}>
          <div className={styles.progressInfo}>
            <span className={styles.questionCounter}>
              Question {currentQuestion + 1} of {(quiz.questions || []).length}
            </span>
            <span className={styles.progressPercentage}>
              {(quiz.questions || []).length > 0 ? Math.round(((currentQuestion + 1) / (quiz.questions || []).length) * 100) : 0}%
            </span>
          </div>
          <div className={styles.progressBar} ref={progressBarRef}>
            <motion.div 
              className={styles.progressFill}
              initial={{ width: 0 }}
              animate={{ 
                width: `${(quiz.questions || []).length > 0 ? ((currentQuestion + 1) / (quiz.questions || []).length) * 100 : 0}%` 
              }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion}
          className={styles.questionCard}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
        >
          <span className={styles.questionNumber}>
            Question {currentQuestion + 1} of {(quiz.questions || []).length}
          </span>
          
          <div className={styles.questionPrompt}>
            <h3>
              {normalizedQuestion.prompt}
            </h3>
          </div>

          <div className={styles.choicesGrid}>
            {(normalizedQuestion.choices || []).map((choice, idx) => {
              const isSelected = userAnswers === idx;
              const isCorrect = normalizedCorrect.includes(idx);
              const showCorrect = showFeedback && isCorrect;
              const showIncorrect = showFeedback && isSelected && !isCorrect;
              
              return (
                <motion.div
                  key={idx}
                  className={`${styles.choiceCard} 
                    ${isSelected ? styles.selected : ''} 
                    ${showCorrect ? styles.correct : ''}
                    ${showIncorrect ? styles.incorrect : ''}
                    ${answersLocked ? styles.disabled : ''}`}
                  whileHover={!answersLocked ? { scale: 1.02 } : {}}
                  whileTap={!answersLocked ? { scale: 0.98 } : {}}
                  onClick={() => !answersLocked && handleAnswerChange(idx)}
                  style={{ cursor: answersLocked ? 'not-allowed' : 'pointer' }}
                >
                  <div className={styles.choiceIndicator}></div>
                  <span className={styles.choiceText}>{choice}</span>
                </motion.div>
              );
            })}
          </div>

          {showFeedback && (
            <motion.div 
              className={styles.explanation}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className={styles.explanationHeader}>
                <BookOpen size={16} />
                <span>Explanation</span>
              </div>
              <p className={styles.explanationContent}>
                {normalizedQuestion.explanation || 
                  `The correct answer is: ${normalizedQuestion.choices[normalizedCorrect[0]]}`}
              </p>
            </motion.div>
          )}

          {/* Navigation */}
          <div className={styles.navigation}>
            <button 
              className={`${styles.navBtn} ${styles.secondaryBtn}`}
              onClick={prevQuestion}
              disabled={currentQuestion === 0}
            >
              <ArrowLeft size={18} />
              Previous
            </button>
            
            <div className={styles.centerInfo}>
              {userAnswers === undefined ? (
                <span className={styles.hint}>Select an answer or skip</span>
              ) : !showFeedback ? (
                <button 
                  className={`${styles.navBtn} ${styles.secondaryBtn}`}
                  onClick={handleSubmit}
                  style={{ padding: '10px 20px' }}
                >
                  Check Answer
                </button>
              ) : null}
            </div>
            
            <button 
              className={`${styles.navBtn} ${styles.secondaryBtn}`}
              onClick={nextQuestion}
            >
              {currentQuestion === (quiz.questions || []).length - 1 ? 'Finish' : 'Next'}
              <ArrowRight size={18} />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default EnhancedQuizDeck;
